import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import { cn } from "~/lib/utils";

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  children?: React.ReactNode;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, children, id, ...props }, ref) => {
  // Generate a unique ID if not provided
  const checkboxId = id || React.useId();

  const checkbox = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={checkboxId}
      data-slot="checkbox"
      className={cn(
        "size-4 shrink-0 rounded-sm border transition-colors duration-200",
        "flex items-center justify-center",
        "data-[state=checked]:border-brand-700 data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700",
        "data-[state=unchecked]:border-gray-200 data-[state=unchecked]:bg-transparent",
        "hover:data-[state=checked]:border-brand-500 hover:data-[state=checked]:bg-transparent hover:data-[state=checked]:text-brand-500",
        "hover:data-[state=unchecked]:border-brand-700 hover:data-[state=unchecked]:bg-brand-50",
        "focus:outline-none focus:ring focus:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-70",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.33317 2.5L3.74984 7.08333L1.6665 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  // If no children, return just the checkbox
  if (!children) {
    return checkbox;
  }

  // If children provided, wrap in a label
  return (
    <div className="flex text-sm items-start gap-2 cursor-pointer">
      <div className="h-5 flex flex-row items-center">{checkbox}</div>
      <label
        htmlFor={checkboxId}
        className={cn(
          "font-normal cursor-pointer select-none",
          "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        )}
      >
        {children}
      </label>
    </div>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
